import { useQuery } from '@tanstack/react-query'
import { api, get, hostError, post, put, type FailoverGroup, type GroupParams, type Host, type NodeInfo } from '../../api'
import { t, type Key } from '../../i18n'
import type { State } from '../../ui'
import { isNodeGroup } from './groups'
import type { Readiness, ReadinessItem, ReadinessMember } from './groupTypes'

/*
 * Making and composing node groups (GROUP-UI-3; owner's answers 01.11, AUDIT §17 NG-12…NG-20):
 * create a group by region + index, add and remove members, delete an empty group, the default
 * group behind the automatic region, the group's readiness and the warnings a change would raise
 * (`?dry_run=1`, owner V2/V3: warn, never forbid).
 *
 * GROUP-DEFAULT's API is the one of its PR #550 (not merged yet when this was written):
 * GET / PUT /api/admin/ingest/default-group, `default` on a group of GET /api/admin/failover,
 * `readiness {state, items[{key, state, text, action}], members[], cert, dns}` on the group view,
 * `warnings[{code, text}]` of a dry run. The center's `text` is English; the console says the
 * same in EN / ES / RU from the code / key and the facts it has, and falls back to the center's
 * text only for a code it does not know. A center without GROUP-DEFAULT (404 on the default
 * group) gets no dry runs: it would apply a `?dry_run=1` request for real.
 */

export const OTHER_REGION = '__other__'
export const DEFAULT_GROUP_KEY = ['ingest-default-group'] as const

/** GET /api/admin/ingest/default-group (#550 DefaultGroupView). */
export interface DefaultGroup {
  /** g<id> of the default group; null = none (the center's addresses) */
  group: string | null
  name?: string
  fqdn?: string
  state: 'unset' | 'active' | 'empty' | 'missing' | (string & {})
  live_members: number
  automatic: { source: 'group' | 'center' | (string & {}); rtmp?: string; rtmps?: string }
  warnings: DryWarning[]
}

export interface DryWarning {
  code:
    | 'auto_ingest_last' | 'hub_outputs' | 'mixed_sites' | 'group_no_live_members' | 'group_cert' | 'group_no_lua' | 'group_dns'
    | 'default_group_removed' | 'auto_center' | 'free_group' | 'default_group_inactive' | (string & {})
  /** the center's English sentence */
  text: string
}

/** Warnings that cost users their ingest or every platform a reconnect: the action turns danger (NG-18 C). */
const DANGER = new Set(['auto_ingest_last', 'hub_outputs'])
export const isDanger = (ws: DryWarning[]) => ws.some((w) => DANGER.has(w.code))

const enc = encodeURIComponent

export const groupAdminApi = {
  create: (body: { region: string; index: number; params: Partial<GroupParams>; totp?: string }) =>
    post<FailoverGroup>('/api/admin/failover/groups', { kind: 'group', ...body }),
  join: (node: string, group: string, totp?: string) => put<NodeInfo>(`/api/admin/nodes/${enc(node)}/failover`, { mode: 'group', group, totp }),
  leave: (node: string, totp?: string) => put<NodeInfo>(`/api/admin/nodes/${enc(node)}/failover`, { mode: 'none', totp }),
  remove: (id: string, totp?: string) => api<void>('DELETE', `/api/admin/failover/groups/${enc(id)}`, { totp }),
  defaultGroup: () => get<DefaultGroup>('/api/admin/ingest/default-group'),
  setDefault: (group: string | null, totp?: string) => put<DefaultGroup>('/api/admin/ingest/default-group', { group, totp }),
  dryRun: (method: string, path: string, body: unknown) =>
    api<{ dry_run?: boolean; warnings?: DryWarning[] }>(method, `${path}${path.includes('?') ? '&' : '?'}dry_run=1`, body),
}

/** The default group (GROUP-DEFAULT); `supported` false on a center without it. */
export function useDefaultGroup() {
  const q = useQuery({ queryKey: DEFAULT_GROUP_KEY, queryFn: groupAdminApi.defaultGroup, retry: false, refetchInterval: 15_000, staleTime: 5_000 })
  return { data: q.data, supported: q.isSuccess, query: q }
}

/**
 * The warnings a change would raise (dry run; owner V2/V3). Sent only when the center has
 * GROUP-DEFAULT (`supported`) — an older center would apply a dry run for real. No code needed.
 */
export function useDryRun(req: { method: string; path: string; body: unknown } | null, supported: boolean) {
  const q = useQuery({
    queryKey: ['dry-run', req?.method, req?.path, JSON.stringify(req?.body ?? null)],
    queryFn: () => groupAdminApi.dryRun(req!.method, req!.path, req!.body),
    enabled: !!req && supported,
    retry: false,
    staleTime: 10_000,
  })
  return { warnings: q.data?.warnings ?? [], pending: !!req && supported && q.isPending }
}

/* ── words ───────────────────────────────────────────────────────────────── */

/** A text with its variables: rendered by tn() so names and numbers come out mono (rule 8). */
export type Words = [Key, Record<string, string | number>?]

/** What the console knows around a warning: the node and the group of the change. */
export interface WarnCtx {
  name?: string
  group?: string
}

/**
 * A warning in words by its code: a bold first sentence and what it means (09-voice: effect,
 * cause, action). The center's `text` carries details the code does not (sites, counts): it is
 * shown as is only for a code the console does not know.
 */
export function warningText(w: DryWarning, ctx: WarnCtx = {}): { title: Words; body?: Words; raw?: string } {
  const name = ctx.name || '—'
  const group = ctx.group || '—'
  switch (w.code) {
    case 'auto_ingest_last':
      return { title: ['gx.warn.lastAuto', { name }], body: ['gx.warn.lastAutoB'] }
    case 'hub_outputs':
      return { title: ['gx.warn.hub', { name }], body: ['gx.warn.hubB'] }
    case 'mixed_sites':
      return { title: ['gx.warn.sites'], body: ['gx.warn.sitesB', { name, group }] }
    case 'group_no_live_members':
      return { title: ['gx.warn.noLive', { group }], body: ['gx.warn.noLiveB'] }
    case 'group_cert':
      return { title: ['gx.warn.cert', { group }], body: ['gx.warn.certB'] }
    case 'group_no_lua':
      return { title: ['gx.r.lua'], body: ['gx.r.luaB'] }
    case 'group_dns':
      return { title: ['gx.warn.dns', { group }] }
    case 'default_group_removed':
      return { title: ['gx.warn.defaultRemoved', { group }], body: ['gx.warn.defaultRemovedB'] }
    case 'default_group_inactive':
      return { title: ['gx.warn.defaultGone'], body: ['gx.warn.defaultRemovedB'] }
    case 'auto_center':
      return { title: ['gx.warn.autoCenter'], body: ['gx.warn.autoCenterB'] }
    case 'free_group':
      return { title: ['gx.warn.free'], body: ['gx.warn.freeB'] }
  }
  return { title: ['gx.warn.other'], raw: w.text }
}

/** Why a node cannot join this group (owner B-3, B-4: another group and no host agent block too). */
export function unfitReason(n: NodeInfo, group: FailoverGroup, opts: { host?: Host; prov: boolean; groups: FailoverGroup[] }): string | undefined {
  if (n.status === 'revoked') return t('gx.why.revoked')
  if (n.roles?.includes('integrations')) return t('gx.why.integrations')
  const f = n.failover
  if (f?.mode === 'group' && f.group && f.group !== group.id) {
    const other = opts.groups.find((g) => g.id === f.group)
    return t('gx.why.otherGroup', { group: other?.name ?? f.group_name ?? f.group })
  }
  if (n.egress === 'direct') return t('gx.why.direct')
  if (!opts.prov || !opts.host) return t('gx.why.noAgent')
  return undefined
}

/** The zone of the ingest names: from a group that has its FQDN, else from the default group. */
export function ingestZone(groups: FailoverGroup[], dg?: DefaultGroup): string {
  for (const g of groups) if (g.fqdn && g.fqdn.startsWith(`${g.name}.`)) return g.fqdn.slice(g.name.length + 1)
  if (dg?.fqdn && dg.name && dg.fqdn.startsWith(`${dg.name}.`)) return dg.fqdn.slice(dg.name.length + 1)
  return ''
}

/** Indexes of node groups of a region that exist already. */
export const takenIndexes = (groups: FailoverGroup[], region: string) =>
  groups.filter((g) => isNodeGroup(g) && g.region === region).map((g) => g.index ?? 0)

/** An error of the create / join / leave / delete / default endpoints, in words. */
export function adminErrorText(e: unknown, ctx: { name?: string } = {}): string {
  const h = hostError(e)
  const raw = h?.error ?? (e as Error)?.message ?? ''
  const name = ctx.name ?? ''
  if (/a node already has this name/.test(raw)) return t('gx.err.nodeName', { name })
  if (/a group with this name exists/.test(raw)) return t('gx.err.exists', { name })
  if (/region and index/.test(raw)) return t('gx.err.format')
  if (/egress: a group member/.test(raw)) return t('gx.why.direct')
  if (/the group has members/.test(raw)) return t('gx.err.hasMembers')
  if (/integrations node/.test(raw)) return t('gx.why.integrations')
  if (/node is revoked/.test(raw)) return t('gx.why.revoked')
  if (/[Cc]luster/.test(raw)) return t('gx.err.cluster')
  if (/only a node group .* can be the default/.test(raw)) return t('gx.err.notNodeGroup')
  return t('group.err.other', { why: raw })
}

/* ── readiness (NG-16 C: only what needs work, OK folded into one line) ──── */

export type ReadyWord = 'ok' | 'pending' | 'warn' | 'fail'
export const READY_LOOK: Record<ReadyWord, { state: State; word: Key }> = {
  ok: { state: 'live', word: 'gx.ready.ok' },
  pending: { state: 'start', word: 'gx.ready.pending' },
  warn: { state: 'warn', word: 'gx.ready.warn' },
  fail: { state: 'fail', word: 'gx.ready.fail' },
}

const CHECK_NAME: Record<string, Key> = {
  cert: 'gx.check.cert', dns: 'gx.check.dns', members: 'gx.check.members', private_net: 'gx.check.private', bandwidth: 'gx.check.bandwidth', default: 'gx.check.default',
}

/** The short name of a check (for the folded OK line). */
export const checkName = (key: string) => (CHECK_NAME[key] ? t(CHECK_NAME[key]!) : key)

/** One line of the readiness: a check that needs work, in words, with the node to open. */
export interface ReadyLine {
  key: string
  state: 'fail' | 'warn' | 'pending'
  title: Words
  body?: Words
  /** the center's own detail (an error text) */
  extra?: string
  node?: string
}

const memberName = (m: ReadinessMember) => m.name || m.node

/**
 * The checks of a group that need work, in words (EN / ES / RU) from the facts of the readiness
 * (members, cert, dns): one line per member problem, so each gets its node card to open.
 * A certificate being issued is WAITING, not a warning.
 */
export function readinessLines(r: Readiness, ctx: { fqdn: string; group: string }): ReadyLine[] {
  const out: ReadyLine[] = []
  const ms = r.members ?? []
  for (const it of r.items) {
    if (it.state === 'ok') continue
    const state = it.state
    switch (it.key) {
      case 'cert': {
        const st = r.cert?.status
        if (st === 'pending' && ms.length > 0) out.push({ key: 'cert', state: 'pending', title: ['gx.r.certPending'] })
        else if (st === 'off') out.push({ key: 'cert', state, title: ['gx.r.certOff'], body: ['gx.r.certOffB'] })
        else if (st === 'error') out.push({ key: 'cert', state, title: ['gx.r.certFail'], extra: r.cert?.error })
        else if (ms.length === 0) out.push({ key: 'cert', state, title: ['gx.r.certNoMembers'] })
        break
      }
      case 'dns': {
        const d = r.dns
        if (!d || d.error) out.push({ key: 'dns', state, title: ['gx.r.dnsUnknown'], extra: d?.error })
        else if (!d.record) out.push({ key: 'dns', state, title: ['gx.r.dnsMissing', { fqdn: ctx.fqdn }], body: ['gx.r.dnsMissingB'] })
        else if (!d.lua) out.push({ key: 'dns', state, title: ['gx.r.lua'], body: ['gx.r.luaB'] })
        break
      }
      case 'members': {
        if (ms.length === 0) {
          out.push({ key: 'members', state, title: ['gx.r.noMembers'], body: ['gx.r.noMembersB'] })
          break
        }
        if (!ms.some((m) => m.takes)) out.push({ key: 'members', state, title: ['gx.r.noLive', { n: ms.length }], body: ['gx.r.noLiveB'] })
        for (const m of ms) {
          if (!m.via_center) out.push({ key: 'members', state: 'warn', title: ['gx.r.direct', { name: memberName(m) }], body: ['gx.r.directB'], node: m.node })
          if (!m.ingest) out.push({ key: 'members', state: 'warn', title: ['gx.r.noIngest', { name: memberName(m) }], body: ['gx.r.noIngestB'], node: m.node })
          if (m.agent === 'offline') out.push({ key: 'members', state: 'warn', title: ['gx.r.agentOff', { name: memberName(m) }], body: ['gx.r.agentOffB'], node: m.node })
        }
        break
      }
      case 'private_net': {
        const sites = [...new Set(ms.map((m) => m.site).filter(Boolean))].sort() as string[]
        if (sites.length > 1) out.push({ key: 'private_net', state, title: ['gx.r.sites', { n: sites.length, sites: sites.join(', ') }], body: ['gx.r.sitesB'] })
        else for (const m of ms.filter((x) => !x.private_addr)) out.push({ key: 'private_net', state, title: ['gx.r.private', { name: memberName(m) }], body: ['gx.r.privateB'], node: m.node })
        break
      }
      case 'bandwidth':
        for (const m of ms.filter((x) => !x.bandwidth_mbps)) out.push({ key: 'bandwidth', state, title: ['gx.r.bandwidth', { name: memberName(m) }], body: ['gx.r.bandwidthB'], node: m.node })
        break
      case 'default':
        if (state === 'fail') out.push({ key: 'default', state, title: ['gx.r.defaultNoLive', { group: ctx.group }], body: ['gx.r.defaultNoLiveB'] })
        else out.push({ key: 'default', state, title: ['gx.r.defaultApex'], body: ['gx.r.defaultApexB'] })
        break
    }
  }
  // a problem the facts did not explain still gets one line: its name and the center's words
  for (const it of r.items) {
    if (it.state !== 'ok' && !out.some((l) => l.key === it.key)) out.push({ key: it.key, state: it.state, title: ['gx.r.other', { check: checkName(it.key) }], extra: it.text })
  }
  return out.sort((a, b) => rank(a.state) - rank(b.state))
}

const rank = (s: ReadyLine['state']) => (s === 'fail' ? 0 : s === 'warn' ? 1 : 2)

/** The checks that are OK (the folded line); "default group" only for the default group. */
export function readinessOk(items: ReadinessItem[], isDefault: boolean): string[] {
  return items.filter((i) => i.state === 'ok' && (i.key !== 'default' || isDefault)).map((i) => checkName(i.key))
}

/** The group's word: NOT READY on any fail, CHECK on a warning, WAITING while something is issued, else OK. */
export function readinessWord(lines: ReadyLine[], state?: 'ok' | 'warn' | 'fail'): ReadyWord {
  if (state === 'fail' || lines.some((l) => l.state === 'fail')) return 'fail'
  if (lines.some((l) => l.state === 'warn')) return 'warn'
  if (lines.some((l) => l.state === 'pending')) return 'pending'
  return state === 'warn' ? 'warn' : 'ok'
}
